import { Injectable } from '@angular/core';
import {
  Groupbuy,
  GroupbuyInstance,
  GroupbuyInstanceReturn,
  GroupbuyInstanceTxn,
  GroupbuyStatistics,
} from '@mommy/models/groupbuy.model';
import { VenderGBService } from '@mommy/services/vender/vender-gb.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { parse } from 'date-fns';
import * as _ from 'lodash';
import {
  CancelGBI,
  CreateGBI,
  DoGBIItemComment,
  DoGBITxnAdd,
  DoGBITxnReceived,
  DoGBITxnReturn,
  GetGBIListBySite,
  GetGBIReturnList,
  GetGBIStatisticsBySiteCodes,
  GetGBITxnList,
  GetGBListByTeam,
  GetMyGBITxnList,
  JoinGBI,
  LeaveGBI,
  StopGBI,
} from './vender-gb.actions';

export interface VenderGBStateModel {
  GBList: Groupbuy[];
  GBIList: GroupbuyInstance[];
  GBITxnList: GroupbuyInstanceTxn[];
  GBIReturnList: GroupbuyInstanceReturn[];
  GBIStatistics: GroupbuyStatistics[];
  MyGBITxnList: GroupbuyInstanceTxn[];
}

const defaultVenderGBState = (): VenderGBStateModel => {
  return {
    GBList: [],
    GBIList: [],
    GBITxnList: [],
    GBIReturnList: [],
    GBIStatistics: [],
    MyGBITxnList: [],
  };
};

@State<VenderGBStateModel>({
  name: 'VenderGBState',
  defaults: defaultVenderGBState(),
})
@Injectable()
export class VenderGBState {
  constructor(private venderGBService: VenderGBService) {}

  @Selector()
  static _gbList(state: VenderGBStateModel) {
    return state.GBList;
  }

  @Selector()
  static _gbiStatistics(state: VenderGBStateModel) {
    return state.GBIStatistics;
  }

  @Selector([VenderGBState._gbList])
  static gbList(_gbList: Groupbuy[]) {
    console.log('_gbList', _gbList);
    return _.chain(_gbList)
      .map((item) => {
        let new_item = _.cloneDeep(item);
        new_item.backgroundImagePath =
          `url(${new_item.banner_url})` + ', url(assets/img3/default_prod.png)';
        new_item.reward_point = _.sumBy(
          item.reward_design?.rewards,
          'reward_value'
        );
        //console.log('new_item.reward_point', new_item.reward_point);
        new_item.cost = item.product_design?.products?.[0]?.cost;
        new_item.list_price = item.product_design?.products?.[0]?.list_price;
        new_item.sales_price_limit =
          item.product_design?.products?.[0]?.sales_price_limit;
        new_item.product_spec = item.product_design?.products?.[0]?.spec;

        return new_item;
      })
      .orderBy(['created_at'], ['desc'])
      .value();
  }

  @Selector()
  static _gbiList(state: VenderGBStateModel) {
    return state.GBIList;
  }

  @Selector()
  static _gbiTxnList(state: VenderGBStateModel) {
    return state.GBITxnList;
  }

  @Selector()
  static _myGBITxnList(state: VenderGBStateModel) {
    return state.MyGBITxnList;
  }

  // 撈取正在進行中的團購清單
  // 定義: gbi_status = DELIVERED and (instance_end_time or instance_manual_closed_at) > 10 天 或是 未成團(gbi_status is CANCEL)
  @Selector([VenderGBState._gbiList])
  static wipGBIList(_gbiList: GroupbuyInstance[]) {
    console.log('wipGBIList', _gbiList);

    const result = _.chain(_gbiList)
      .filter((item) => {
        if (item.gbi_status === 'DELIVERED') {
          let end_time = new Date(item.instance_end_time);
          // 如果有 instance_manual_closed_at 則以它為主
          if (item.instance_manual_closed_at) {
            end_time = new Date(item.instance_manual_closed_at);
          }
          let now = new Date();
          let diff = now.getTime() - end_time.getTime();
          let diffDays = diff / (1000 * 3600 * 24);
          return diffDays < 10; // 10天內 仍然顯示
        } else if (item.gbi_status === 'CANCEL') {
          return false;
        } else {
          return true;
        }
      })
      .map((item) => {
        let new_item = _.cloneDeep(item);
        new_item.backgroundImagePath =
          `url(${new_item.banner_url})` + ', url(assets/img3/default_prod.png)';
        new_item.reward_point = _.sumBy(
          item.reward_design?.rewards,
          'reward_value'
        );
        console.log('new_item.reward_point', new_item.reward_point);
        new_item.cost = item.product_design?.products?.[0]?.cost;
        new_item.list_price = item.product_design?.products?.[0]?.list_price;
        new_item.item_name = item.product_design?.products?.[0]?.name;
        new_item.sales_price_limit =
          item.product_design?.products?.[0]?.sales_price_limit;
        new_item.product_spec = item.product_design?.products?.[0]?.spec;

        return new_item;
      })
      .orderBy(['created_at'], ['desc'])
      .value();

    console.log('result', result);
    return result;
  }

  // 撈取 all 團購清單
  // 上架中/已下架 定義: now > instance_end_time or manual_closed_at 代表已下架
  @Selector([VenderGBState._gbiList])
  static allGBIList(_gbiList: GroupbuyInstance[]) {
    console.log('allGBIList', _gbiList);

    const result = _.chain(_gbiList)
      .map((item) => {
        let new_item = _.cloneDeep(item);
        new_item.backgroundImagePath =
          `url(${new_item.banner_url})` + ', url(assets/img3/default_prod.png)';
        new_item.reward_point = _.sumBy(
          item.reward_design?.rewards,
          'reward_value'
        );
        console.log('new_item.reward_point', new_item.reward_point);
        new_item.cost = item.product_design?.products?.[0]?.cost;
        new_item.list_price = item.product_design?.products?.[0]?.list_price;
        new_item.item_name = item.product_design?.products?.[0]?.name;
        new_item.sales_price_limit =
          item.product_design?.products?.[0]?.sales_price_limit;
        new_item.product_spec = item.product_design?.products?.[0]?.spec;

        const str_closed_at =
          item.instance_manual_closed_at || item.instance_end_time;
        const closed_at = parse(
          str_closed_at,
          'yyyy-MM-dd HH:mm:ss',
          new Date()
        );
        new_item.is_closed = new Date() > closed_at;
        return new_item;
      })
      .orderBy(['created_at'], ['desc'])
      .value();

    console.log('result', result);
    return result;
  }

  // 撈取團購的 txn list
  @Selector([VenderGBState._gbiTxnList])
  static gbiTxnList(_gbiTxnList: GroupbuyInstanceTxn[]) {
    return _gbiTxnList;
  }

  @Selector()
  static _gbiReturnList(state: VenderGBStateModel) {
    return state.GBIReturnList;
  }

  // 團購的預約名單(整合 txn and return)
  @Selector([VenderGBState._gbiTxnList, VenderGBState._gbiReturnList])
  static gbiMemberList(
    _gbiTxnList: GroupbuyInstanceTxn[],
    _gbiReturnList: GroupbuyInstanceReturn[]
  ) {
    console.log('gbiMemberList', _gbiTxnList, _gbiReturnList);

    return _.chain(_gbiTxnList)
      .map((txn) => {
        let new_txn = _.cloneDeep(txn);
        // 如果有退貨紀錄，則加入退貨紀錄, 且將 txn_status overwrite to 'RETURNED'
        let gbi_return: GroupbuyInstanceReturn = <GroupbuyInstanceReturn>_.find(
          _gbiReturnList,
          {
            groupbuy_instance_txn_id: txn.groupbuy_instance_txn_id,
          }
        );
        console.log('gbi_return', gbi_return);
        if (gbi_return) {
          new_txn.gbi_return = gbi_return;
          new_txn.txn_status = 'RETURNED';
        }
        return new_txn;
      })
      .orderBy(['txn_datetime'], ['asc'])
      .value();
  }

  // 撈取團購數量統計
  @Selector([VenderGBState._gbiStatistics])
  static gbiStatistics(_gbiStatistics: GroupbuyStatistics[]) {
    return _.chain(_gbiStatistics)
      .groupBy('site_code')
      .map((instances, site_code) => {
        return {
          site_code,
          instance_count: _.sumBy(instances, 'instance_count'),
        };
      })
      .value();
  }

  // 撈取我的 txn list
  @Selector([VenderGBState._myGBITxnList])
  static myGBITxnList(_myGBITxnList: GroupbuyInstanceTxn[]) {
    return _myGBITxnList;
  }

  // ===============================================================================
  //                             Action
  // ===============================================================================

  // 取得團購方案清單
  @Action(GetGBListByTeam)
  async GetGBListByTeam(
    ctx: StateContext<VenderGBStateModel>,
    { site_code }: GetGBListByTeam
  ) {
    console.log('[Action] GetGBListByTeam', site_code);

    try {
      const result = await this.venderGBService.get_gb_list_by_site({
        site_code,
      });
      console.log('result', result);

      ctx.patchState({
        GBList: result?.data,
      });
    } catch (error) {
      console.error('GetGBListByTeam error', error);
      throw error;
    }
  }

  // 建立團購
  @Action(CreateGBI)
  async CreateGBI(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: CreateGBI
  ) {
    console.log('[Action] CreateGBI', payload);

    try {
      const result = await this.venderGBService.create_gbi(payload);
      console.log('result', result);
    } catch (error) {
      console.error('CreateGBI error', error);
      throw error;
    }
  }

  // 停止團購(manual stop, submit order)
  @Action(StopGBI)
  async StopGBI(ctx: StateContext<VenderGBStateModel>, { payload }: StopGBI) {
    console.log('[Action] StopGBI', payload);

    try {
      const result = await this.venderGBService.stop_gbi(payload);
      console.log('result', result);
    } catch (error) {
      console.error('StopGBI error', error);
      throw error;
    }
  }

  // 取消團購(manual stop, cancel all orders)
  @Action(CancelGBI)
  async CancelGBI(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: CancelGBI
  ) {
    console.log('[Action] CancelGBI', payload);

    try {
      const result = await this.venderGBService.cancel_gbi(payload);
      console.log('result', result);
    } catch (error) {
      console.error('CancelGBI error', error);
      throw error;
    }
  }

  // 取得團購instance清單
  @Action(GetGBIListBySite)
  async GetGBIListBySite(
    ctx: StateContext<VenderGBStateModel>,
    { site_code }: GetGBIListBySite
  ) {
    console.log('[Action] GetGBIListBySite', site_code);

    try {
      const result = await this.venderGBService.get_gbi_list_by_site({
        site_code,
      });
      console.log('result', result);

      ctx.patchState({
        GBIList: result?.data,
      });
    } catch (error) {
      console.error('GetGBIListBySite error', error);
      throw error;
    }
  }

  // 取得團購Instance Txn list
  @Action(GetGBITxnList)
  async GetGBITxnList(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: GetGBITxnList
  ) {
    console.log('[Action] GetGBITxnList', payload);

    try {
      const result = await this.venderGBService.get_gbi_txn_list(payload);
      console.log('result', result);

      ctx.patchState({
        GBITxnList: result?.data,
      });
    } catch (error) {
      console.error('GetGBITxnList error', error);
      throw error;
    }
  }

  // 取得團購Instance Return list
  @Action(GetGBIReturnList)
  async GetGBIReturnList(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: GetGBIReturnList
  ) {
    console.log('[Action] GetGBIReturnList', payload);

    try {
      const result = await this.venderGBService.get_gbi_return_list(payload);
      console.log('result', result);

      ctx.patchState({
        GBIReturnList: result?.data,
      });
    } catch (error) {
      console.error('GetGBIReturnList error', error);
      throw error;
    }
  }

  // 退貨團購(醫護人員使用)
  @Action(DoGBITxnReturn)
  async DoGBITxnReturn(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: DoGBITxnReturn
  ) {
    console.log('[Action] DoGBITxnReturn', payload);

    try {
      const result = await this.venderGBService.return_gbi_txn(payload);
      console.log('result', result);
    } catch (error) {
      console.error('DoGBITxnReturn error', error);
      throw error;
    }
  }

  // 加入團購,新增預約名單(醫護人員使用)
  @Action(DoGBITxnAdd)
  async DoGBITxnAdd(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: DoGBITxnAdd
  ) {
    console.log('[Action] DoGBITxnAdd', payload);

    try {
      const result = await this.venderGBService.add_gbi_txn(payload);
      console.log('result', result);
    } catch (error) {
      console.error('DoGBITxnAdd error', error);
      throw error;
    }
  }

  // 加入團購,新增預約名單(個案自行加入使用)
  @Action(JoinGBI)
  async JoinGBI(ctx: StateContext<VenderGBStateModel>, { payload }: JoinGBI) {
    console.log('[Action] JoinGBI', payload);

    try {
      const result = await this.venderGBService.join_gbi(payload);
      console.log('result', result);
    } catch (error) {
      console.error('JoinGBI error', error);
      throw error;
    }
  }

  // 退出團購(個案自行退出使用)
  @Action(LeaveGBI)
  async LeaveGBI(ctx: StateContext<VenderGBStateModel>, { payload }: LeaveGBI) {
    console.log('[Action] LeaveGBI', payload);

    try {
      const result = await this.venderGBService.leave_gbi(payload);
      console.log('result', result);
    } catch (error) {
      console.error('LeaveGBI error', error);
      throw error;
    }
  }

  // 取得我的團購清單
  @Action(GetMyGBITxnList)
  async GetMyGBITxnList(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: GetMyGBITxnList
  ) {
    console.log('[Action] GetMyGBITxnList', payload);

    try {
      const result = await this.venderGBService.get_my_joiners(payload);
      console.log('result', result);

      ctx.patchState({
        MyGBITxnList: result?.data,
      });
    } catch (error) {
      console.error('GetMyGBITxnList error', error);
      throw error;
    }
  }

  // 評論商品
  @Action(DoGBIItemComment)
  async DoGBIItemComment(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: DoGBIItemComment
  ) {
    console.log('[Action] DoGBIItemComment', payload);

    try {
      const result = await this.venderGBService.gbi_item_comment(payload);
      console.log('result', result);
    } catch (error) {
      console.error('DoGBIItemComment error', error);
      throw error;
    }
  }

  // 團購標註已領取(醫護人員使用)
  @Action(DoGBITxnReceived)
  async DoGBITxnReceived(
    ctx: StateContext<VenderGBStateModel>,
    { payload }: DoGBITxnReceived
  ) {
    console.log('[Action] DoGBITxnReceived', payload);

    try {
      const result = await this.venderGBService.mark_gbi_txn_received(payload);
      console.log('result', result);
    } catch (error) {
      console.error('DoGBITxnReceived error', error);
      throw error;
    }
  }

  // 取得團購數量統計 by site_codes(多個)
  @Action(GetGBIStatisticsBySiteCodes)
  async GetGBIStatisticsBySiteCodes(
    ctx: StateContext<VenderGBStateModel>,
    { site_codes }: GetGBIStatisticsBySiteCodes
  ) {
    console.log('[Action] GetGBIStatisticsBySiteCodes', site_codes);

    try {
      const result = await this.venderGBService.get_gb_stat_by_site_codes({
        site_codes,
      });
      console.log('result', result);

      ctx.patchState({
        GBIStatistics: result?.data,
      });
    } catch (error) {
      console.error('GetGBIStatisticsBySiteCodes error', error);
      throw error;
    }
  }
}
